/* The Dashboard (ADR 0030, design handoff 13): a board of dashboard widgets each
   person arranges. Top bar (title, Account switcher, Gross | Net, Add widget,
   Customise), then a 12-column grid packed densely in layout order. Customise
   edits a draft — drag a widget's bar onto another to swap places, drag the
   corner to resize, ⚙ for its settings — that Done saves and Cancel drops.
   Phones read the same layout as one column and cannot edit it. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { Account } from '../api/client';
import { AccountPicker } from '../components/AccountPicker';
import { C, FONT } from '../components/ChatAssistant/compact/tokens';
import { font as f } from '../components/ChatAssistant/compact/style';
import { Icon, Seg } from '../components/ChatAssistant/compact/kit';
import { ReloginModal } from '../components/Onboarding/ReloginModal';
import { toastQueue } from '../components/ToastProvider';
import type { MoneyMode } from '../components/Statistics/unified/model';
import { kyivToday } from '../components/Statistics/unified/model';
import { useAccounts } from '../context/AccountsContext';
import { useAuth } from '../context/AuthContext';
import { useCrmHost } from '../crm/CrmHostContext';
import { useMediaMatch } from '../hooks/useIsNarrow';
import { useStatsAccess } from '../hooks/useStatsAccess';
import { hasReconnectAccess } from '../permissions';
import { accountName, isPending } from './attention';
import { DashContext, type DashEnv } from './context';
import {
  COLS, GAP, MAX_WIDGETS, PHONE_QUERY, ROW, TYPES, allowedTypes, cellsToPx, newWidget, resize,
  type Access, type AnyCfg, type Widget, type WidgetType,
} from './model';
import { SettingsPanel } from './SettingsPanel';
import { useWidgetTitle } from './titles';
import { canSeeMoney, canSwitchAi, useAccountPermissions } from './useAccountPermissions';
import { useBoard } from './useBoard';
import { useSnoozes } from './useSnoozes';
import { useSourceCache } from './useSourceCache';
import { WidgetView } from './widgets/WidgetView';
import type { FrameProps } from './WidgetFrame';
import './dashboard.css';

function useGridWidth() {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return { ref: setEl, width };
}

/** While a draft is unsaved: confirm before following a link or closing the tab. */
function useLeaveGuard(active: boolean, message: string) {
  useEffect(() => {
    if (!active) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]');
      if (!a || (a as HTMLAnchorElement).target === '_blank') return;
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    document.addEventListener('click', onClick, true);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [active, message]);
}

function AddWidgetMenu({ types, full, onAdd }: { types: WidgetType[]; full: boolean; onAdd: (t: WidgetType) => void }) {
  const { t } = useTranslation('dashboardWidgets');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open]);
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button
        type="button"
        className="dbw-btn"
        onClick={() => setOpen((o) => !o)}
        disabled={full}
        title={full ? t('addFull', { count: MAX_WIDGETS }) : undefined}
        aria-expanded={open}
      >
        <Icon name="plus" size={14} color={C.purpleLt} />{t('addWidget')}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
          <div role="menu" className="dbw-menu" style={{ position: 'absolute', top: 38, right: 0, zIndex: 31, width: 320 }}>
            <span style={{ padding: '6px 10px 4px', font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{t('widgetTypes')}</span>
            {types.map((type) => (
              <button key={type} type="button" role="menuitem" className="dbw-menu-row" onClick={() => { setOpen(false); onAdd(type); }}>
                <span style={{ width: 30, height: 30, flex: 'none', borderRadius: 7, background: C.tint16, color: C.purplePale, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={TYPES[type].icon} size={14} />
                </span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ font: f(600, 13), color: C.text }}>{t(`types.${type}.name`)}</span>
                  <span style={{ font: f(400, 11.5), color: C.sub }}>{t(`types.${type}.desc`)}</span>
                </span>
                <span style={{ font: f(500, 10.5), color: C.muted, whiteSpace: 'nowrap' }}>{t('minSize', { size: `${TYPES[type].min[0]}×${TYPES[type].min[1]}` })}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function DashboardBoard() {
  const { t } = useTranslation('dashboardWidgets');
  const { t: ts } = useTranslation('statsUnified');
  const navigate = useNavigate();
  const crm = useCrmHost();
  const { isSuperAdmin } = useAuth();
  const { accounts: allAccounts, loaded, subscriptionMap, updateAccount, refresh: refreshAccounts } = useAccounts();
  const statsAccess = useStatsAccess();
  const access: Access | null = statsAccess === null ? null : { stats: statsAccess, crm: crm.access.state === 'ok' };
  const board = useBoard(access);
  const { snoozes, setSnoozed } = useSnoozes();
  const phone = useMediaMatch(PHONE_QUERY);
  const titleOf = useWidgetTitle();

  /* ── Account switcher: All or one Account, kept in the URL ─────── */
  const [params, setParams] = useSearchParams();
  const pickedId = params.get('account') ?? undefined;
  const picked = pickedId ? allAccounts.find((a) => a.account_id === pickedId) : undefined;
  const accountId = picked?.account_id;
  const accounts = useMemo(() => (picked ? [picked] : allAccounts), [picked, allAccounts]);
  const setAccount = (id: string | undefined) => {
    const next = new URLSearchParams(params);
    if (id) next.set('account', id); else next.delete('account');
    setParams(next, { replace: true });
  };
  const pickerAccounts = useMemo(
    () => allAccounts.map((a) => ({ account_id: a.account_id, name: accountName(a) })).sort((a, b) => a.name.localeCompare(b.name)),
    [allAccounts],
  );

  /* ── Account state the widgets share ───────────────────────────── */
  const [throttled, setThrottled] = useState<ReadonlySet<string>>(new Set());
  const loadThrottle = useCallback(() => {
    api.getThrottleStatus().then((r) => setThrottled(new Set(r.throttled_account_ids))).catch(() => {});
  }, []);
  useEffect(() => { loadThrottle(); }, [loadThrottle]);

  const perms = useAccountPermissions(allAccounts);
  const hasStatsOn = useCallback((id: string) => isSuperAdmin || canSeeMoney(perms.getPermissions(id)), [isSuperAdmin, perms]);
  const canToggle = useCallback((id: string) => isSuperAdmin || canSwitchAi(perms.getPermissions(id)), [isSuperAdmin, perms]);
  const statsScope = perms.known ? { n: accounts.filter((a) => hasStatsOn(a.account_id)).length, of: accounts.length } : null;

  const [today, setToday] = useState(kyivToday);
  const cache = useSourceCache(accountId);
  const { reloadAll } = cache;
  // Only on the Refresh button: no timer and no re-read on tab focus. Each re-read is
  // a burst of /statistics requests, and a Dashboard left open on a screen kept
  // sending them all day (backend pools 503'd).
  const refresh = useCallback(() => {
    setToday(kyivToday());
    reloadAll();
    loadThrottle();
  }, [reloadAll, loadThrottle]);

  const [relogin, setRelogin] = useState<Account | null>(null);
  const openAccount = useCallback((a: Account) => {
    navigate(isPending(a) ? `/accounts/new?resume=${a.account_id}` : crm.mode === 'crm' ? crm.crmHref(a.account_id) : `/account/${a.account_id}`);
  }, [navigate, crm]);
  const reconnect = useCallback((a: Account) => {
    if (isSuperAdmin || hasReconnectAccess(perms.getPermissions(a.account_id))) setRelogin(a);
    else openAccount(a);
  }, [isSuperAdmin, perms, openAccount]);

  const setEnabled = useCallback(async (targets: Account[], enabled: boolean) => {
    const todo = targets.filter((a) => a.is_enabled !== enabled);
    todo.forEach((a) => updateAccount(a.account_id, { is_enabled: enabled }));
    const results = await Promise.allSettled(todo.map((a) => api.toggleAccount(a.account_id, enabled)));
    let ok = 0;
    let failed = 0;
    results.forEach((res, i) => {
      const a = todo[i];
      if (!a) return;
      if (res.status === 'fulfilled') { ok += 1; updateAccount(a.account_id, res.value); }
      else { failed += 1; updateAccount(a.account_id, { is_enabled: !enabled }); }
    });
    return { ok, failed };
  }, [updateAccount]);

  /* ── Customise ─────────────────────────────────────────────────── */
  const editing = board.editing && !phone;
  const [cfgId, setCfgId] = useState<string | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  // Read by dragover, which can fire before React re-renders with `drag`.
  const dragRef = useRef<string | null>(null);
  const lastSwap = useRef<string | null>(null);
  const [resizing, setResizing] = useState<string | null>(null);
  const grid = useGridWidth();
  const colW = (grid.width - GAP * (COLS - 1)) / COLS;
  const types = access ? allowedTypes(access) : [];
  useLeaveGuard(board.dirty, t('leaveConfirm'));

  const addWidget = (type: WidgetType) => {
    const w = type === 'accounts' && !access?.stats ? newWidget('accounts', { cols: ['status'] }) : newWidget(type);
    if (!board.editing) board.startEdit();
    board.setDraft((ws) => [w, ...ws].slice(0, MAX_WIDGETS));
    setCfgId(w.id);
    window.scrollTo({ top: 0 });
  };
  const removeWidget = (id: string) => {
    board.setDraft((ws) => ws.filter((w) => w.id !== id));
    setCfgId((c) => (c === id ? null : c));
  };
  const setCfg = (id: string, patch: Partial<AnyCfg>) =>
    board.setDraft((ws) => ws.map((w) => (w.id === id ? ({ ...w, cfg: { ...w.cfg, ...patch } } as Widget) : w)));
  const done = async () => {
    const ok = await board.doneEdit();
    if (ok) setCfgId(null);
    else toastQueue.add({ title: t('saveFailed') });
  };
  const cancel = () => {
    board.cancelEdit();
    setCfgId(null);
  };
  const flipMoney = async (m: MoneyMode) => {
    if (!(await board.setMoney(m))) toastQueue.add({ title: t('moneySaveFailed') });
  };

  const frameFor = (w: Widget): FrameProps => {
    const size = phone ? { width: grid.width, height: cellsToPx(1, w.h, 0).height } : cellsToPx(w.w, w.h, colW);
    const [minW, minH] = TYPES[w.type].min;
    return {
      widget: w,
      title: titleOf(w),
      ...size,
      span: phone ? 1 : w.w,
      editing,
      selected: cfgId === w.id,
      dragging: drag === w.id,
      atMin: resizing === w.id && (w.w === minW || w.h === minH),
      onSettings: () => setCfgId(w.id),
      onRemove: () => removeWidget(w.id),
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', w.id);
        const box = (e.currentTarget as HTMLElement).parentElement;
        if (box) e.dataTransfer.setDragImage(box, 24, 16);
        lastSwap.current = null;
        dragRef.current = w.id;
        setDrag(w.id);
      },
      onDragOver: (e) => {
        const dragged = dragRef.current;
        if (!dragged) return;
        e.preventDefault();
        // Each widget swaps in once per pass over it, so mixed sizes don't flicker back and forth.
        if (dragged === w.id || lastSwap.current === w.id) return;
        lastSwap.current = w.id;
        board.setDraft((ws) => {
          const from = ws.findIndex((x) => x.id === dragged);
          const to = ws.findIndex((x) => x.id === w.id);
          if (from < 0 || to < 0) return ws;
          const next = [...ws];
          const [moved] = next.splice(from, 1);
          if (moved) next.splice(to, 0, moved);
          return next;
        });
      },
      onDragEnd: () => { dragRef.current = null; lastSwap.current = null; setDrag(null); },
      onResizeStart: (e) => {
        e.preventDefault();
        e.stopPropagation();
        const sx = e.clientX;
        const sy = e.clientY;
        const w0 = w.w;
        const h0 = w.h;
        const move = (ev: PointerEvent) => {
          const nw = w0 + Math.round((ev.clientX - sx) / (colW + GAP));
          const nh = h0 + Math.round((ev.clientY - sy) / (ROW + GAP));
          board.setDraft((ws) => ws.map((x) => {
            if (x.id !== w.id) return x;
            const r = resize(x, nw, nh);
            return r.w === x.w && r.h === x.h ? x : r;
          }));
        };
        const up = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          setResizing(null);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        setResizing(w.id);
      },
    };
  };

  const env: DashEnv | null = access && {
    accounts,
    subs: subscriptionMap,
    throttled,
    accountId,
    money: board.money,
    access,
    statsScope,
    hasStatsOn,
    canToggle,
    editing,
    today,
    openAccount,
    reconnect,
    setEnabled,
    snoozes,
    setSnoozed,
    cache,
  };

  const ready = !!env && board.ready && loaded;
  const cfgWidget = editing && cfgId ? board.widgets.find((w) => w.id === cfgId) : undefined;

  return (
    <div className="dbw-root" style={{ fontFamily: FONT, color: C.text }}>
      <div className={`dbw-bar ${editing ? 'is-editing' : ''}`}>
        {editing ? (
          <>
            <span className="dbw-pill" title={t('hint')}><Icon name="edit-2" size={12} />{t('customising')}</span>
            <button type="button" className="dbw-btn is-quiet" onClick={() => { board.resetToDefault(); setCfgId(null); }}>
              <Icon name="rotate-ccw" size={13} color={C.purpleLt} />{t('resetDefault')}
            </button>
            <span className="dbw-hint">{t('hint')}</span>
          </>
        ) : (
          <>
            <h1 className="dbw-title">{t('title')}</h1>
            <AccountPicker accounts={pickerAccounts} value={accountId} onChange={setAccount} align="left" />
            <Seg
              size="md"
              value={board.money}
              onChange={(m) => void flipMoney(m)}
              options={[
                { id: 'gross', label: ts('money.gross'), title: ts('money.grossTip') },
                { id: 'net', label: ts('money.net'), title: ts('money.netTip') },
              ]}
            />
            <span style={{ flex: 1 }} />
            <button type="button" className="dbw-btn is-quiet" onClick={refresh} title={ts('refresh')}>
              <Icon name="refresh-cw" size={13} color={C.purpleLt} />{ts('refresh')}
            </button>
            <Link to="/dashboard?classic=1" className="dbw-old-link">{t('oldLink')}</Link>
          </>
        )}
        {!phone && ready && (
          <>
            <AddWidgetMenu types={types} full={board.widgets.length >= MAX_WIDGETS} onAdd={addWidget} />
            {editing ? (
              <>
                <button type="button" className="dbw-btn is-quiet" onClick={cancel}>{t('cancel')}</button>
                <button type="button" className="dbw-primary" onClick={() => void done()} disabled={board.saving}>{t('done')}</button>
              </>
            ) : (
              <button type="button" className="dbw-btn is-quiet" onClick={board.startEdit} disabled={board.loadFailed}>
                <Icon name="layout" size={14} color={C.purpleLt} />{t('customise')}
              </button>
            )}
          </>
        )}
      </div>

      <div className="dbw-body" style={{ paddingRight: cfgWidget ? 364 : undefined }}>
        {board.loadFailed && (
          <div role="alert" className="dbw-notice">
            <Icon name="alert-circle" size={14} />
            <span style={{ flex: 1 }}>{t('loadFailed')}</span>
            <button type="button" className="dbw-link-btn" onClick={board.reload}>{t('widget.retry')}</button>
          </div>
        )}
        <div style={{ position: 'relative' }}>
          {editing && (
            <div aria-hidden style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`, gap: GAP, pointerEvents: 'none' }}>
              {Array.from({ length: COLS }, (_, i) => <span key={i} style={{ borderRadius: 6, background: 'color-mix(in srgb, var(--om-accent) 5%, transparent)' }} />)}
            </div>
          )}
          <div
            ref={grid.ref}
            className="dbw-grid"
            style={{ position: 'relative', display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : `repeat(${COLS}, minmax(0, 1fr))`, gridAutoRows: ROW, gridAutoFlow: 'row dense', gap: GAP }}
          >
            {!ready && [12, 8, 4].map((span, i) => (
              <div key={i} className="dbw-skeleton" style={{ gridColumn: `span ${phone ? 1 : span}`, gridRow: `span ${i ? 7 : 6}` }} />
            ))}
            {ready && env && grid.width > 0 && (
              <DashContext.Provider value={env}>
                {board.widgets.map((w) => <WidgetView key={w.id} {...frameFor(w)} />)}
              </DashContext.Provider>
            )}
            {ready && !board.widgets.length && (
              <div className="dbw-empty" style={{ gridColumn: `span ${phone ? 1 : COLS}`, gridRow: 'span 5' }}>
                <Icon name="layout" size={22} color={C.purpleLt} />
                <span style={{ font: f(600, 13), color: C.text }}>{t('empty.title')}</span>
                <span style={{ font: f(400, 12), color: C.muted }}>{t('empty.body')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {cfgWidget && access && (
        <SettingsPanel
          widget={cfgWidget}
          access={access}
          onChange={(patch) => setCfg(cfgWidget.id, patch)}
          onRemove={() => removeWidget(cfgWidget.id)}
          onClose={() => setCfgId(null)}
        />
      )}

      {relogin && (
        <ReloginModal
          accountId={relogin.account_id}
          platform={relogin.platform}
          onClose={() => setRelogin(null)}
          onConnected={() => {
            toastQueue.add({ title: t('needs.reconnected', { name: accountName(relogin) }) });
            setRelogin(null);
            void refreshAccounts();
          }}
        />
      )}
    </div>
  );
}
